import React from 'react';
import PropTypes from 'prop-types';
import HSL from '@haptik/helpers/build/esm/hslBuilder';
import Button from 'relements/build/esm/ui/button';
import Accordion from 'relements/build/esm/ui/accordion';
import Table from 'relements/build/esm/ui/table';
import Dropdown from 'relements/build/esm/input/dropdown';
import Checkbox from 'relements/build/esm/input/checkbox';
import Chips from 'relements/build/esm/input/chips';
import Loader from 'relements/build/esm/decorators/loader';

import Papa from 'papaparse';

// Components
import connection from '@src/connection';
import Icon from '@src/components/Icon';

// Constants
import {
  NODE_TYPES,
  CONTEXT_CLEAR_NODE,
  END_OF_QUERY,
} from '@src/constants/MogamboConstants';

// Utils
import { generateNodeName } from '@src/utils/NodeUtils';

// Local
import * as maps from './maps';
import styles from './UploadNodes.scss';

const NODE_TYPE_OPTIONS = [
  {
    id: NODE_TYPES.BOT_GENERAL,
    label: 'FAQs',
    requiredProp: 'domain',
  },
  {
    id: NODE_TYPES.STORY_GENERAL,
    label: 'Skill Steps',
    requiredProp: 'story',
  },
  {
    id: NODE_TYPES.REGULAR,
    label: 'Graph Steps',
    requiredProp: 'subStory',
  },
];

const NODE_DATA_PREVIEW_COLUMNS = [
  {
    title: 'Step Name',
    width: '15%',
  },
  {
    title: 'Question',
    width: '30%',
  },
  {
    title: 'Answer',
    width: '45%',
  },
  {
    title: '',
    width: '10%',
  },
];

const NEGATIVE_SIGN = 'NEGATIVE';

@Loader({})
@connection(maps.listenToStores, maps.getStateFromStores)
export default class UploadNodes extends React.Component {
  constructor() {
    super();
    this.state = {
      nodeType: NODE_TYPE_OPTIONS[0],
      nodeData: [],
      [CONTEXT_CLEAR_NODE.KEY]: true,
      [END_OF_QUERY.KEY]: true,
      start: true,
    };
  }

  componentDidMount() {
    setTimeout(() => {
      maps.updateComponentModal({
        headerRight: this._generateUploadButton(),
      });
    }, 0);
  }

  render() {
    let body = null;
    switch (this.state.nodeType.id) {
      case NODE_TYPES.BOT_GENERAL:
        body = this._renderDomainGeneralNodes();
        break;
      case NODE_TYPES.STORY_GENERAL:
        body = this._renderStoryGeneralNodes();

        break;
      case NODE_TYPES.REGULAR:
        body = this._renderRegularNodes();
        break;
      default:
        break;
    }

    return (
      <div className={styles.container}>
        <div className={styles.description}>
          <Dropdown
            withSearch
            className={styles.input}
            options={this._filterNodeOptions()}
            label="Step Type"
            placeholder="Please select the type of Steps to be created"
            optionKey="label"
            value={this.state.nodeType}
            onChange={(value) => this._updateNodeType(value)}
          />
          {body}
        </div>
        <input
          ref={(ref) => {
            if (ref) this._inputRef = ref;
          }}
          onChange={this._onFileChange}
          type="file"
          style={{
            visibility: 'hidden',
            width: '1px',
            height: '1px',
          }}
        />
        {this._renderNodeDataPreview()}
      </div>
    );
  }

  _updateNodeType = (value) => {
    this.setState({
      nodeType: value,
      nodeData: [],
    });
    setTimeout(() => {
      maps.updateComponentModal({
        headerRight: this._generateUploadButton(),
      });
    }, 0);
  };

  _generateUploadButton = () => {
    return (
      <Button onClick={this._openFileChooser} type={Button.TYPES.PRIMARY}>
        Upload File
      </Button>
    );
  };

  _generateCreateNodeButton = () => {
    return (
      <Button onClick={this._onSave} type={Button.TYPES.PRIMARY}>
        Save & Create Steps
      </Button>
    );
  };

  _filterNodeOptions = () => {
    return NODE_TYPE_OPTIONS.filter(
      (option) =>
        this.props[option.requiredProp] && this.props[option.requiredProp].title
    );
  };

  _toggleStartNode = () => {
    const start = !this.state.start;
    const nodeData = this.state.nodeData.map((data) => {
      data.start = start;
      return data;
    });
    this.setState({ start, nodeData });
  };

  _toggleContextClearNode = () => {
    const context_clear = !this.state[CONTEXT_CLEAR_NODE.KEY];
    const nodeData = this.state.nodeData.map((data) => {
      data[CONTEXT_CLEAR_NODE.KEY] = context_clear;
      return data;
    });
    this.setState({ [CONTEXT_CLEAR_NODE.KEY]: context_clear, nodeData });
  };

  _toggleEndOfQueryNode = () => {
    const query_end = !this.state[END_OF_QUERY.KEY];
    const nodeData = this.state.nodeData.map((data) => {
      data[END_OF_QUERY.KEY] = query_end;
      return data;
    });
    this.setState({ [END_OF_QUERY.KEY]: query_end, nodeData });
  };

  _renderDomainGeneralNodes = () => {
    return (
      <div>
        The Steps you upload will be created as 
        {' '}
        <b>FAQs</b>
        {' '}
        for
        {' '}
        <b>{this.props.domain.title}</b>
        .
        <div className={styles.row}>
          <Checkbox.Item
            value={
              this.state[
                this.props.nodeContextMenuEnabled
                  ? END_OF_QUERY.KEY
                  : CONTEXT_CLEAR_NODE.KEY
              ]
            }
            onChange={
              this.props.nodeContextMenuEnabled
                ? this._toggleEndOfQueryNode
                : this._toggleContextClearNode
            }
            label={`Mark All as ${
              this.props.nodeContextMenuEnabled
                ? END_OF_QUERY.VALUE
                : CONTEXT_CLEAR_NODE.VALUE
            }`}
          />
        </div>
      </div>
    );
  };

  _renderStoryGeneralNodes = () => {
    return (
      <div>
        The Steps you upload will be created as 
        {' '}
        <b>Skill Steps</b>
        {' '}
        for
        {' '}
        <b>{this.props.story.title}</b>
        {' '}
        in
        <b>{this.props.domain.title}</b>
        .
        <div className={styles.row}>
          <Checkbox.Item
            value={
              this.state[
                this.props.nodeContextMenuEnabled
                  ? END_OF_QUERY.KEY
                  : CONTEXT_CLEAR_NODE.KEY
              ]
            }
            onChange={
              this.props.nodeContextMenuEnabled
                ? this._toggleEndOfQueryNode
                : this._toggleContextClearNode
            }
            label={`Mark All as ${
              this.props.nodeContextMenuEnabled
                ? END_OF_QUERY.VALUE
                : CONTEXT_CLEAR_NODE.VALUE
            }`}
          />
        </div>
      </div>
    );
  };

  _renderRegularNodes = () => {
    return (
      <div>
        The Steps will be created as Graph Steps under the current Skill
        {' '}
        <b>{this.props.subStory.title}</b>
        .
        <div className={styles.row}>
          <Checkbox.Item
            value={this.state.start}
            onChange={this._toggleStartNode}
            label="Mark All as Start Step"
          />
          <Checkbox.Item
            value={
              this.state[
                this.props.nodeContextMenuEnabled
                  ? END_OF_QUERY.KEY
                  : CONTEXT_CLEAR_NODE.KEY
              ]
            }
            onChange={
              this.props.nodeContextMenuEnabled
                ? this._toggleEndOfQueryNode
                : this._toggleContextClearNode
            }
            label={`Mark All as ${
              this.props.nodeContextMenuEnabled
                ? END_OF_QUERY.VALUE
                : CONTEXT_CLEAR_NODE.VALUE
            }`}
          />
        </div>
      </div>
    );
  };

  _removeRow = (indexToRemove) => {
    const filteredNodeData = this.state.nodeData.filter(
      (_, index) => index !== indexToRemove
    );
    this.setState({
      nodeData: filteredNodeData,
    });
  };

  _renderNodeDataPreview = () => {
    if (this.state.nodeData.length === 0) return null;
    const tableData = this.state.nodeData.map((node, index) => {
      const row = {
        id: index,
        columns: [],
      };
      row.columns.push(this._generateColumn(<div>{node.title}</div>));
      row.columns.push(
        this._generateColumn(this._renderNodeUserResponses(node.userResponses))
      );
      row.columns.push(
        this._generateColumn(
          this._renderNodeBotResponses(node.botResponses, node.quickReplies)
        )
      );
      row.columns.push(
        this._generateColumn(
          <div>
            <Icon iconType="trash" onClick={() => this._removeRow(index)} />
          </div>
        )
      );
      return row;
    });

    return (
      <div className={styles.dataPreview}>
        <Accordion>
          <Accordion.Item
            key="node-data-preview-table"
            index="node-data-preview-table"
            active={this.state.previewTableExpanded}
            onChange={() =>
              this.setState({
                previewTableExpanded: !this.state.previewTableExpanded,
              })}
            renderHeader={({ ExpandIcon }) => (
              <div>
                Preview Step Data 
                {' '}
                <ExpandIcon />
              </div>
            )}
          >
            <div className={styles.tableWrapper}>
              <Table rows={tableData} columns={NODE_DATA_PREVIEW_COLUMNS} />
            </div>
          </Accordion.Item>
        </Accordion>
      </div>
    );
  };

  _renderNodeUserResponses = (responses) => {
    let extraCount = 0;
    if (responses.length > 1) {
      extraCount = responses.length - 1;
    }
    return (
      <div>
        <div className={styles.sampleResponse}>{responses[0]}</div>
        {extraCount > 0 && (
          <div className={styles.extraResponses}>
            And 
            {' '}
            {extraCount}
            {' '}
            more variations
          </div>
        )}
      </div>
    );
  };

  _renderNodeBotResponses = (responses, quickReplies) => {
    let extraCount = 0;
    if (responses.length > 1) {
      extraCount = responses.length - 1;
    }
    return (
      <div>
        <div>
          <HSL value={responses[0]} disabled readOnly />
        </div>
        {extraCount > 0 && (
          <div className={styles.extraResponses}>
            And 
            {' '}
            {extraCount}
            {' '}
            more variations
          </div>
        )}
        {quickReplies && quickReplies.length > 0 && (
          <Chips disabled value={quickReplies} />
        )}
      </div>
    );
  };

  _generateColumn = (content) => ({
    content,
  });

  _onFileChange = () => {
    if (window.File && window.FileReader && window.FileList && window.Blob) {
      // Great success! All the File APIs are supported.
      const reader = new FileReader();
      const fileDetails = this._inputRef.files[0];

      reader.onload = () => {
        try {
          Papa.parse(reader.result, {
            header: true,
            trimHeaders: true,
            dynamicTyping: true,
            delimiter: ',',
            complete: (results) => {
              this._createNodesFromFile(results.meta.fields, results.data);
              this.props.deactivateLoader();
              if (results.errors.length === 1) {
                maps.createAlert('There was 1 error in parsing the CSV file');
              } else if (results.errors.length > 1) {
                maps.createAlert(
                  `There were ${results.errors.length} errors in reading the CSV file`
                );
              }
            },
          });
        } catch (e) {
          this.props.deactivateLoader();
          maps.createAlert('Incorrect CSV file uploaded');
        }
        this._inputRef.value = '';
      };

      reader.readAsText(fileDetails);
      this.props.activateLoader();
    } else {
      maps.createAlert('File upload is not supported on this browser.');
    }
  };

  _createNodesFromFile = (fields, rows) => {
    let allFieldsPresent = true;
    const compulsoryFields = ['node_name', 'question', 'answer'];
    compulsoryFields.forEach((columnName) => {
      if (allFieldsPresent && !fields.includes(columnName)) {
        maps.createAlert(
          `The CSV does not have a '${columnName}' column.`,
          3500,
          NEGATIVE_SIGN
        );
        allFieldsPresent = false;
      }
    });
    if (!allFieldsPresent) return;

    const cleanedRows = rows.filter((row) => {
      // filter out rows if data in any of the compulsory columns is empty
      if (compulsoryFields.filter((field) => !row[field]).length !== 0) {
        return false;
      }

      // filter out rows if there is no valid node name present
      if (row.node_name.replace(/[^a-zA-Z0-9_]/g, '').length < 3) {
        return false;
      }

      return true;
    });

    const nodeData = cleanedRows.map((row) => {
      const convertedData = {
        nodeType: this.state.nodeType.id,
        domainName: this.props.domain.title,
        storyName: this.props.story.title,
        subStoryId: this.props.subStory.id,
        title: row.node_name,
        systemName: generateNodeName(
          this.state.nodeType.id,
          row.node_name,
          this.props.domain.title,
          this.props.story.title
        ),
        userResponses: row.question.split('|'),
        botResponses: [row.answer],
        start: this.state.start,
        quickReplies: (row.quick_replies && row.quick_replies.split('|')) || [],
        apiIntegrationUrl: row.api_integration_url || '',
        apiKey: row.api_key || '',
      };

      if (!this.props.nodeContextMenuEnabled) {
        convertedData[CONTEXT_CLEAR_NODE.KEY] = this.state[
          CONTEXT_CLEAR_NODE.KEY
        ];
      } else {
        convertedData[END_OF_QUERY.KEY] = this.state[END_OF_QUERY.KEY];
      }

      const linkHSL = this._generateButtonLinkHSL(
        row.link_text,
        row.link_button,
        row.link_url
      );
      if (linkHSL) {
        convertedData.botResponses = convertedData.botResponses.map(
          (response) => {
            const responseList = response.split('<m>');
            responseList.push(linkHSL);
            return responseList.join('<m>');
          }
        );
      }

      switch (this.state.nodeType.id) {
        case NODE_TYPES.BOT_GENERAL:
          break;

        case NODE_TYPES.STORY_GENERAL:
          break;

        case NODE_TYPES.REGULAR:
          convertedData.start = this.state.start;
          break;

        default:
          break;
      }
      return convertedData;
    });

    this.setState({
      nodeData,
    });

    setTimeout(() => {
      maps.updateComponentModal({
        headerRight: this._generateCreateNodeButton(),
      });
    }, 0);
  };

  _onSave = async () => {
    const { nodeData } = this.state;
    let error = false;

    this.props.activateLoader();
    for (let i = 0; i < nodeData.length; i++) {
      const data = nodeData[i];
      if (!data.saved) {
        try {
          const response = await maps.createNode(data);
          nodeData[i].saved = true;
        } catch (e) {
          maps.createAlert(e, 3500, NEGATIVE_SIGN);
          error = true;
          break;
        }
      }
    }

    maps.createAlert('Step data saved.');
    this.props.deactivateLoader();

    if (!error) {
      maps.closeComponentModal();
      if (this.props.story.id) {
        maps.getSubstoryGraph(this.props.story.id, () => {
          maps.updateGraph({ REFRESH: true });
        });
      }
    } else {
      this.setState({
        nodeData,
      });
    }
  };

  _openFileChooser = () => {
    if (this._inputRef) {
      this._inputRef.click();
    }
  };

  _isInvalidString = (str) => {
    return !str || str.length === 0;
  };

  _generateButtonLinkHSL = (text, actionableText, url) => {
    if (
      this._isInvalidString(text) ||
      this._isInvalidString(actionableText) ||
      this._isInvalidString(url)
    ) {
      return null;
    }

    const hslObject = {
      text: `${text}`,
      type: 'BUTTON',
      data: {
        items: [
          {
            actionable_text: `${actionableText}`,
            location_required: false,
            is_default: 0,
            uri: 'LINK',
            type: 'APP_ACTION',
            payload: {
              url: `${url}`,
              gogo_message: '',
            },
          },
        ],
      },
    };
    return JSON.stringify(hslObject);
  };
}

UploadNodes.propTypes = {
  deactivateLoader: PropTypes.func,
  activateLoader: PropTypes.func,
  domain: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  story: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  subStory: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
};
